
import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { createClient } from '@/lib/supabase-client'
import { type UserProfile } from '../types'
import Sidebar from './Sidebar'
import DashboardHeader from './DashboardHeader'

export default function IntegrationsPage() {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const supabase = createClient()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      navigate('/auth/login')
      return
    }

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()

    if (profileData) {
      setProfile({
        ...profileData,
        email: session.user.email || '',
      } as UserProfile)
    }

    setLoading(false)
  }

  const integrations = [
    {
      name: 'Make.com',
      description: 'Automate alt text generation in your Make.com workflows. Available for all plans!',
      icon: '⚙️',
      logo: '/make-color.png',
      color: 'purple',
      status: 'available',
      link: '/make',
      installLink: 'https://www.make.com/en/hq/app-invitation/13425288b9cf9bb84e01060543c1b621',
      external: false,
    },
    {
      name: 'WordPress Plugin',
      description: 'Auto-generate alt text for your WordPress media library',
      icon: '📝',
      logo: '/wordpress-logo3.webp',
      color: 'blue',
      status: 'available',
      link: 'https://wordpress.org/plugins/gistmag-alt-text-generator/',
      external: true,
    },
    {
      name: 'WordPress TTS Reader',
      description: 'Add text-to-speech functionality to your WordPress blog posts',
      icon: '🎙️',
      logo: '/wordpress-logo3.webp',
      color: 'indigo',
      status: 'available',
      link: '/docs/integrations/wordpress-tts',
      downloadLink: 'https://api.gistmag.co.uk/downloads/wordpress-tts-plugin',
      external: false,
    },
      {
        name: 'WordPress Gist Blog',
      description: 'Generate images, excerpt, category, and tags for blog posts using AI',
      icon: '📝',
      logo: '/wordpress-logo3.webp',
      color: 'purple',
      status: 'available',
      link: '/docs/integrations/wordpress-blog-meta-generator',
      downloadLink: 'https://api.gistmag.co.uk/downloads/wordpress-blog-meta-generator-plugin',
      external: false,
    },
    {
      name: 'Shopify App',
      description: 'Automatically generate SEO-friendly alt text for all your product images using AI',
      icon: '🛍️',
      logo: '/shopify.png',
      color: 'green',
      status: 'available',
      link: 'https://apps.shopify.com/gistmag-alt-text-generator',
      external: true,
    },
    {
      name: 'Chrome Extension',
      description: 'Right-click any image to generate alt text in Chrome',
      icon: '🌐',
      logo: '/Google-Chrome-Logo-for-tech-themed-designs-web-development-graphics-transparent-PNG-image-jpg.png',
      color: 'purple',
      status: 'available',
      link: 'https://chromewebstore.google.com/detail/gistmag-alt-text-generato/fdlepmdamhcpnlnkpmpkgdhfelogkahi',
      external: true,
    },
    {
      name: 'Microsoft Edge Extension',
      description: 'Right-click any image to generate alt text in Edge',
      icon: '🌐',
      logo: '/png-clipart-edge-browser-logo-tech-companies-thumbnail.png',
      color: 'blue',
      status: 'available',
      link: 'https://microsoftedge.microsoft.com/addons/detail/gistmag-alt-text-generato/piejclnjcjmlgneiecnnjgmnknlbpbje',
      external: true,
    },
    {
      name: 'Firefox Extension',
      description: 'Right-click any image to generate alt text in Firefox',
      icon: '🦊',
      logo: '/firefox.png',
      color: 'orange',
      status: 'available',
      link: 'https://addons.mozilla.org/en-GB/firefox/addon/gistmag-alt-text-generator/',
      external: true,
    },
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <Sidebar />
      <div className="lg:pl-64">
        <DashboardHeader title="Integrations" />
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Integrations</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">Connect GistMag with your favorite platforms</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((integration, index) => (
              <div
                key={integration.name}
                className="glass-card rounded-xl p-6 hover-lift animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-16 h-16 ${
                  integration.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  integration.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                  integration.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' :
                  'bg-purple-100 dark:bg-purple-900/30'
                } rounded-xl flex items-center justify-center mb-4 ${(integration as any).logo ? 'p-2' : 'text-2xl'} ${
                  integration.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  integration.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  integration.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                  'text-purple-600 dark:text-purple-400'
                }`}>
                  {(integration as any).logo ? (
                    <img
                      src={(integration as any).logo}
                      alt={integration.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback to emoji if image fails to load
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const parent = target.parentElement
                        if (parent) {
                          parent.innerHTML = `<span class="text-2xl">${integration.icon}</span>`
                        }
                      }}
                    />
                  ) : (
                    <span>{integration.icon}</span>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{integration.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{integration.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    integration.status === 'available'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>
                    {integration.status === 'available' ? 'Available' : 'Coming Soon'}
                  </span>
                  {integration.status === 'available' && (integration as any).downloadLink ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={(integration as any).downloadLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={async (e) => {
                          e.preventDefault();
                          const downloadUrl = (integration as any).downloadLink;
                          // Open in new tab for download
                          const newWindow = window.open(downloadUrl, '_blank');
                          // Attempt to close after download starts (browsers may block this)
                          setTimeout(() => {
                            try {
                              if (newWindow && !newWindow.closed) {
                                newWindow.close();
                              }
                            } catch (err) {
                              // Browser blocked closing, which is expected
                            }
                          }, 3000);
                        }}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1"
                      >
                        Download <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                      {integration.external ? (
                        <>
                          <span className="text-gray-400">|</span>
                          <a
                            href={integration.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1"
                          >
                            View Plugin <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </>
                      ) : (
                        <>
                          <span className="text-gray-400">|</span>
                          <Link
                            to={integration.link}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                          >
                            Learn more →
                          </Link>
                        </>
                      )}
                    </div>
                  ) : integration.status === 'available' && integration.external ? (
                    <a
                      href={integration.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1"
                    >
                      View Plugin <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : integration.status === 'available' && (integration as any).installLink ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={(integration as any).installLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1"
                      >
                        Install <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <span className="text-gray-400">|</span>
                      <Link
                        to={integration.link}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                      >
                        Learn more →
                      </Link>
                    </div>
                  ) : integration.status === 'available' ? (
                    <Link
                      to={integration.link}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                    >
                      Learn more →
                    </Link>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  )
}

